import { useEffect, useRef, useState } from "react";
import { Truck, Users, Star } from "lucide-react";

const stats = [
  { icon: Truck, value: 12000, suffix: "+", label: "Moves Completed", description: "Across Australia" },
  { icon: Users, value: 9800, suffix: "+", label: "Happy Customers", description: "And counting" },
  { icon: Star, value: 4.9, suffix: "", label: "Average Rating", description: "Based on Google Reviews", decimals: 1 },
];

function useCountUp(target: number, duration: number, start: boolean, decimals = 0) {
  const [current, setCurrent] = useState(0);
  const rafRef = useRef<number>();

  useEffect(() => {
    if (!start) return;
    const startTime = performance.now();
    const animate = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCurrent(parseFloat((eased * target).toFixed(decimals)));
      if (progress < 1) rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [start, target, duration, decimals]);

  return current;
}

const StatBlock = ({ icon: Icon, value, suffix, label, description, decimals = 0, animate }: typeof stats[0] & { animate: boolean }) => {
  const count = useCountUp(value, 1800, animate, decimals);
  return (
    <div className="flex flex-col items-center text-center gap-3">
      <div className="w-12 h-12 rounded-full bg-[#FFC300]/12 flex items-center justify-center">
        <Icon className="w-5 h-5 text-[#1a1a1a]" strokeWidth={2} />
      </div>
      <div>
        <span className="text-4xl md:text-5xl font-black text-[#1a1a1a] leading-none tracking-tight">
          {decimals ? count.toFixed(decimals) : Math.round(count).toLocaleString()}{suffix}
        </span>
      </div>
      <div>
        <p className="font-bold text-[#1a1a1a] text-sm">{label}</p>
        <p className="text-[#1a1a1a]/70 text-xs mt-0.5">{description}</p>
      </div>
    </div>
  );
};

const SocialProofStats = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [animate, setAnimate] = useState(false);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setAnimate(true); observer.disconnect(); } },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="bg-white py-20 md:py-28">
      <div className="container max-w-4xl text-center">
        <h2 className="text-3xl md:text-4xl font-black text-[#1a1a1a] leading-tight">
          Trusted Across Australia
        </h2>
        <p className="text-[#1a1a1a]/70 mt-2 text-base mb-14">
          The numbers behind our commitment to better moves.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-12 md:gap-16 max-w-3xl mx-auto">
          {stats.map((s) => (
            <StatBlock key={s.label} {...s} animate={animate} decimals={s.decimals ?? 0} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default SocialProofStats;
